import React from 'react'
import { arrayOf, string, bool } from 'prop-types'
import { Link } from 'gatsby'
import * as yup from 'yup'
import Form from 'react-formal'
import Moment from 'moment'
import momentLocalizer from 'react-widgets-moment'
import { request } from 'graphql-request'
import { Flex, Box, Text } from 'rebass'
import {
  Background,
  FlexWrapper,
  Label,
  Message,
  SubmitButton,
  Input,
  Fieldset,
  TextArea,
  Widget,
  SectionTitle,
  SectionHeading,
  Select
} from './components'
import { Checkbox } from '../ContentForms/components'
import permissions from '../../../lib/permission/schema'
import permissionsGraphqlMutation from '../../../lib/permission/graphql-mutation'
import PermissionControls from '../ContentForms/components/PermissionControls'
import { trackEvent } from '../../../lib/tracker'
import enquiryTypes from '../ContentForms/fixtures/enquiry-types'

Moment.locale('en-gb')
momentLocalizer()

const createModelSchema = ({
  registrationRequired = true,
  locationRequired = true,
  phoneNumberRequired = true,
  additionalInfoRequired = true,
  showPostcode = true,
  enquiryType
}) => {
  const fields = {
    firstName: yup
      .string()
      .default('')
      .required('Please enter your first name')
      .max(75, 'Your name is over 75 characters'),
    lastName: yup
      .string()
      .default('')
      .required('Please enter your last Name')
      .max(75, 'Your last name is over 75 characters'),
    phone: yup
      .string()
      .transform((value) => (value === '' ? undefined : value))
      .min(11, 'Please check your number')
      .max(16, 'Your number is too long'),
    email: yup
      .string()
      .default('')
      .required('Please enter an email address')
      .email('Please check your email address is correct')
      .max(255, 'Your email is over 255 characters'),
    location: yup.string().nullable().default(''),
    question: yup.string().default('').max(1200, 'Your question is too long'),
    enquiryType: yup
      .string()
      .oneOf(enquiryTypes)
      .default(enquiryType || enquiryTypes[0]),
    registration: yup
      .string()
      .default('')
      .max(9, 'Your registration is over 9 characters'),
    postcode: yup.string().default(''),
    permissions: permissions(['email', 'sms', 'phone']),
    termsAndConditions: yup
      .bool()
      .test(
        'terms-agreed',
        'You must agree to the terms and conditions',
        (v) => !!v
      )
  }
  if (registrationRequired)
    fields.registration = fields.registration.required(
      'Please enter your registration'
    )
  if (additionalInfoRequired)
    fields.question = fields.location.required(
      'Please enter required additional information'
    )
  if (showPostcode)
    fields.postcode = fields.postcode.required('Please enter your postcode')

  if (locationRequired)
    fields.location = fields.location.required('Please choose a location')
  if (phoneNumberRequired)
    fields.phone = fields.phone.required('Please enter your phone number')

  return yup.object(fields)
}

Form.addInputTypes(require('react-formal-inputs'))

const submitForm = (data) => {
  const mutation = `mutation {
    createContact(contactRequest: {
      firstName: "${data.firstName}",
      lastName: "${data.lastName}",
      email: "${data.email}",
      postcode: "${data.postcode}",
      phone: "${data.phone ? data.phone : ''}",
      location: "${data.location}",
      question: ${JSON.stringify(data.question)},
      registration: "${data.registration}",
      sourceUrl: "${document.location.toString()}",
      customerEmailType: "${data.customerEmailType}",
      internalEmailType: "${data.internalEmailType}",
      additionalInfoLabel: "${data.additionalInfoLabel}",
      enquiryType: "${data.enquiryType}"
      ${permissionsGraphqlMutation()(data)}
    }) {
      createdDate
    }
  }`
  return request(`${process.env.GATSBY_API_URL}/graphql`, mutation)
}

class ContactForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      submitSuccess: this.props.success,
      submitError: false
    }
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  async handleSubmit(formData) {
    let form = 'Contact'

    if (
      document.location.pathname.includes('business') ||
      document.location.pathname.includes('fleet')
    ) {
      form = 'FleetContact'
    } else if (document.location.pathname.includes('offers')) {
      form = 'OffersContact'
    } else if (document.location.pathname.includes('sell-your-car')) {
      form = 'SellYourCarContact'
    } else if (document.location.pathname.includes('sell-your-van')) {
      form = 'SellYourVanContact'
    }

    trackEvent(form, 'Form', 'Submit', '', {
      location: formData.location,
      enquiry_type: formData.enquiryType,
      source_url: document.location.toString()
    })
    formData.customerEmailType = this.props.customerEmailType || ''
    formData.internalEmailType = this.props.internalEmailType
    formData.additionalInfoLabel = this.props.additionalInfoLabel
    if (this.props.enquiryType) formData.enquiryType = this.props.enquiryType
    const response = await submitForm(formData)
    if (response && response.createContact) {
      this.setState({ submitSuccess: true }, () =>
        this.formTop.scrollIntoView()
      )
    } else {
      this.setState({ submitError: true }, () => this.formTop.scrollIntoView())
    }
  }

  render() {
    const submitButtonText =
      this.props.internalEmailType === 'contact' ? 'Enquire Now' : 'Register'
    const questionLabel = `${this.props.additionalInfoLabel}
      ${this.props.additionalInfoRequired ? '*' : ''}`

    const responseMessages = {
      error: {
        event: `Apologies, but your event registration form wasn't sent and please try again.`,
        contact: `Sorry there was an error. Please try again.`
      },
      success: {
        event: `Thank you for registering for the event and a member of our team will be in touch.`,
        contact: `Thank you for your enquiry, ${
          this.props.customerEmailType
            ? 'you will shortly receive an email acknowledgement and'
            : ''
        } a member of our team will be in touch.`
      }
    }

    const errorMessage = responseMessages.error[this.props.internalEmailType]
    const successMessage =
      responseMessages.success[this.props.internalEmailType]

    const defaultFormValues = {
      enquiryType: this.props.enquiryType || enquiryTypes[0],
      location: this.props.locations.length === 1 ? this.props.locations[0] : ''
    }

    return (
      <Background bg={this.props.bg}>
        <FlexWrapper py={110} px={[2, 2]} flexWrap="wrap" width={'100%'}>
          <Box width={[12 / 12, null, 6 / 12]} pr={[0, null, '10%']}>
            {this.props.heading !== '' && (
              <SectionHeading width={12 / 12} bg={this.props.bg}>
                {this.props.heading}
              </SectionHeading>
            )}
            {this.props.title !== '' && (
              <SectionTitle as="h3" f={[6, 7, 8]} bg={this.props.bg}>
                {this.props.title}
              </SectionTitle>
            )}
            {this.props.children}
          </Box>
          <Box width={[12 / 12, null, 6 / 12]}>
            <a
              id="formTop"
              ref={(ref) => {
                this.formTop = ref
              }}
            />
            {this.state.submitSuccess ? (
              <Text id="contact-success">{successMessage}</Text>
            ) : (
              <Form
                className="f24form"
                schema={createModelSchema(this.props)}
                defaultValue={defaultFormValues}
                onSubmit={this.handleSubmit}
                data-f24name="contact-form"
              >
                {this.state.submitError && <Text>{errorMessage}</Text>}
                <Fieldset>
                  <Flex flexWrap="wrap" width={'100%'} py={[10]}>
                    <Box
                      width={[12 / 12, null, 6 / 12]}
                      pr={[0, null, 20]}
                      mb={10}
                    >
                      <Label bg={this.props.bg}>First name*</Label>
                      <Input
                        bg={this.props.bg}
                        placeholder="Enter first name"
                        name="firstName"
                        aria-label="You are required enter your first name"
                      />
                      <Message for="firstName" />
                    </Box>
                    <Box width={[12 / 12, null, 6 / 12]} mb={10}>
                      <Label bg={this.props.bg}>Last name*</Label>
                      <Input
                        bg={this.props.bg}
                        placeholder="Enter last name"
                        name="lastName"
                        aria-label="You are required enter your last name"
                      />
                      <Message for="lastName" />
                    </Box>
                    <Box width={[12 / 12, null, 12 / 12]} mb={10}>
                      <Label bg={this.props.bg}>Email address*</Label>
                      <Input
                        bg={this.props.bg}
                        placeholder="Enter email address"
                        name="email"
                        aria-label="You are required to enter your email"
                      />
                      <Message for="email" />
                    </Box>
                    {this.props.showPostcode && (
                      <Box
                        width={[12 / 12, null, 6 / 12]}
                        pr={[0, null, 20]}
                        mb={10}
                      >
                        <Label bg={this.props.bg}>Postcode*</Label>
                        <Input
                          bg={this.props.bg}
                          placeholder="Enter postcode"
                          name="postcode"
                          aria-label="Enter postcode"
                        />
                        <Message for="postcode" />
                      </Box>
                    )}
                    <Box width={[12 / 12, null, 6 / 12]} mb={10}>
                      <Label bg={this.props.bg}>
                        Phone number
                        {this.props.phoneNumberRequired && '*'}
                      </Label>
                      <Input
                        bg={this.props.bg}
                        placeholder="Enter phone number"
                        name="phone"
                        aria-label="Enter your phone number"
                      />
                      <Message for="phone" />
                    </Box>

                    {this.props.showEnquiryType && (
                      <Box width={[12 / 12, null, 12 / 12]} mb={10}>
                        <Label bg={this.props.bg}>Enquiry type</Label>
                        <Widget>
                          <Select
                            bg={this.props.bg}
                            placeholder="Enquiry Type"
                            name="enquiryType"
                            data={[
                              ...new Set([
                                this.props.enquiryType,
                                ...enquiryTypes
                              ])
                            ]}
                            type="dropdownlist"
                            aria-label="Select the reason for your enquiry"
                          />
                          <Message for="enquiryType" />
                        </Widget>
                      </Box>
                    )}

                    {this.props.locationRequired && (
                      <Box width={[12 / 12, null, 12 / 12]} mb={10}>
                        {this.props.locationLabel && (
                          <Label bg={this.props.bg}>
                            {this.props.locationLabel}*
                          </Label>
                        )}
                        <Widget>
                          <Select
                            bg={this.props.bg}
                            placeholder="Select location"
                            name="location"
                            data={this.props.locations}
                            type="dropdownlist"
                            aria-label="You are required to select a dealership location"
                          />
                          <Message for="location" />
                        </Widget>
                      </Box>
                    )}

                    <Box width={12 / 12} mb={10}>
                      <Label bg={this.props.bg}>{questionLabel}</Label>
                      <TextArea
                        bg={this.props.bg}
                        name="question"
                        type="textarea"
                        rows={3}
                        aria-label="Enter your question"
                      />
                      <Message for="question" />
                    </Box>
                    {this.props.showRegistrationField && (
                      <Box width={[12 / 12, null, 12 / 12]} mb={10}>
                        <Label bg={this.props.bg}>
                          Registration plate
                          {this.props.registrationRequired && '*'}
                        </Label>
                        <Input
                          bg={this.props.bg}
                          placeholder="Enter registration plate"
                          name="registration"
                          aria-label="Enter registration plate"
                        />
                        <Message for="registration" />
                      </Box>
                    )}
                    <PermissionControls email sms phone bg={this.props.bg} />
                    <Box width={12 / 12} mb={10}>
                      <Label bg={this.props.bg}>
                        <Checkbox
                          name="termsAndConditions"
                          aria-label="You are required to agree to our terms and conditions"
                        />
                        I agree to Citygate’s{' '}
                        <Link to="/legal">terms and conditions</Link>
                      </Label>
                      <Message for="termsAndConditions" />
                    </Box>
                    <Box width={12 / 12} mt={20}>
                      <SubmitButton
                        type="submit"
                        aria-label={submitButtonText}
                        bg={this.props.bg}
                        id="contact-submit"
                      >
                        {submitButtonText}
                      </SubmitButton>
                    </Box>
                  </Flex>
                </Fieldset>
              </Form>
            )}
          </Box>
        </FlexWrapper>
      </Background>
    )
  }
}

ContactForm.defaultProps = {
  heading: '',
  title: '',
  bg: '',
  success: false,
  locations: [],
  registrationRequired: false,
  phoneNumberRequired: false,
  locationRequired: false,
  additionalInfoRequired: false,
  additionalInfoLabel: '',
  showPostcode: false,
  internalEmailType: 'contact',
  showRegistrationField: false,
  showEnquiryType: true
}

ContactForm.propTypes = {
  heading: string,
  title: string,
  bg: string,
  success: bool,
  registrationRequired: bool,
  phoneNumberRequired: bool,
  locationRequired: bool,
  locations: arrayOf(string),
  additionalInfoRequired: bool,
  additionalInfoLabel: string,
  showPostcode: bool,
  internalEmailType: string,
  showRegistrationField: bool,
  enquiryType: string,
  showEnquiryType: bool
}

export default ContactForm
